import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { Clock, CalendarDays, MapPin, AlignLeft, Bell, Pencil, Trash2, Users } from 'lucide-react';
import { DEFAULT_EVENT_COLOR } from '../../extensions/bundled/calendar/calendarStore';
import { eventRange, formatDayLong, formatEventTime } from '../../utils/calendarDates';
import AvatarStack from '../ui/AvatarStack';
import Tooltip from '../ui/Tooltip';
import UserAvatar from '../ui/UserAvatar';
import {
  fetchSentInvitations,
  invitesBlockedFor,
  useCalendarInvitesStore,
} from '../../services/calendarInvitesService';

const WIDTH = 300;

/**
 * "1 HR", "45 MIN", "1 HR 30 MIN", "ALL DAY" — the at-a-glance badge beside the
 * title. Minutes are floored from the real range rather than stored, so a
 * 09:00–10:00 event reads 1 HR whether it was created as a duration or by
 * dragging. Plural forms live in the bundles: German needs "1 Stunde" vs
 * "2 Stunden" where English says "HR" either way.
 */
function durationLabel(event, start, end, t) {
  if (event.all_day) return t('calendar.popover.allDay');
  const mins = Math.max(0, Math.round((end - start) / 60000));
  if (mins === 0) return null;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return t('calendar.popover.durationMin', { count: m });
  if (m === 0) return t('calendar.popover.durationHr', { count: h });
  return `${t('calendar.popover.durationHr', { count: h })} ${t('calendar.popover.durationMin', { count: m })}`;
}

/**
 * Click-an-event popover (PLAN §3.5): title, day + time in the user's
 * timeFormat, location, description, reminders — with Edit / Delete. A
 * portal anchored to the clicked chip's rect; Esc or click-away closes.
 * Deletion confirms via the shared ConfirmModal (hosted by CalendarView —
 * window.confirm THROWS in the packaged app).
 */
export default function EventPopover({
  event,
  anchorRect,
  timeFormat,
  onEdit,
  onDelete,
  onRespondInvite,
  onClose,
  readOnly = false,
}) {
  const { t } = useTranslation();
  const ref = useRef(null);

  // WHO IS ACTUALLY COMING (owner, 31.08.2026). The organizer's sent rows are
  // already fetched and cached per appointment by the editor; the popover just
  // never read them. Cached rows render immediately and a refresh runs behind
  // them, so opening a card is never blocked on the network.
  //
  // `/calendar/invitations/sent` is the ORGANIZER's view: an invitee opening
  // someone else's event gets an error, which is swallowed on purpose — the
  // section simply does not render. Showing attendance to attendees would need
  // a new endpoint and a decision about who may see whose replies.
  const sentRows = useCalendarInvitesStore((s) => s.sentByAppointment[event?.uid]);

  useEffect(() => {
    if (!event?.uid || invitesBlockedFor(event)) return;
    fetchSentInvitations(event.uid).catch(() => {});
  }, [event]);

  const attendance = React.useMemo(() => {
    const rows = (sentRows || []).filter((r) => r.status !== 'cancelled');
    if (rows.length === 0) return null;
    const of = (status) => rows.filter((r) => r.status === status);
    const attending = of('accepted');
    // Rows, not counts: hovering a number should name the people behind it, so
    // each bucket has to keep its members.
    return {
      attending,
      declined: of('declined'),
      awaiting: of('pending'),
      faces: attending.map((r) => ({
        userId: r.user_id,
        name: r.user?.name || r.user?.email || String(r.user_id),
      })),
    };
  }, [sentRows]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Move focus into the dialog so Esc/tab work without an extra click.
  useEffect(() => {
    ref.current?.focus?.();
  }, []);

  if (!event || !anchorRect) return null;

  const vw = window.innerWidth || 1200;
  const vh = window.innerHeight || 800;
  const left = Math.max(8, Math.min(anchorRect.left, vw - WIDTH - 8));
  const top = Math.max(8, Math.min(anchorRect.bottom + 6, vh - 240));
  const { start, end } = eventRange(event);
  const duration = durationLabel(event, start, end, t);

  return ReactDOM.createPortal(
    <>
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 999 }}
        onMouseDown={onClose}
        aria-hidden="true"
      />
      <div
        ref={ref}
        className="cal-popover"
        role="dialog"
        aria-label={event.title || t('calendar.popover.eventDetails')}
        tabIndex={-1}
        style={{ top, left, width: WIDTH }}
      >
        <div className="cal-popover-head">
          <p className="cal-popover-title">
            <span
              className="cal-chip-dot"
              style={{ background: event.color || DEFAULT_EVENT_COLOR }}
              aria-hidden="true"
            />
            {event.title || t('calendar.noTitle')}
            {event.status === 'cancelled' && (
              <span style={{ fontWeight: 400 }}>{t('calendar.popover.cancelled')}</span>
            )}
          </p>
          {duration && <span className="cal-popover-badge">{duration}</span>}
        </div>
        <div className="cal-popover-sep" />
        {/* Time and date are two rows now, not one wrapped block: the time is
            what you look for first, so it gets its own line in tabular figures
            that line up between events. */}
        <div className="cal-popover-row">
          <Clock size={13} className="cal-popover-ico" />
          <span className="cal-popover-time">{formatEventTime(event, timeFormat)}</span>
        </div>
        <div className="cal-popover-row">
          <CalendarDays size={13} className="cal-popover-ico" />
          <span>{formatDayLong(start)}</span>
        </div>
        {attendance && (
          <div className="cal-popover-row">
            <Users size={13} className="cal-popover-ico" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
              <span>
                {[
                  { key: 'attending', rows: attendance.attending, cls: 'cal-popover-attending', always: true },
                  { key: 'declined', rows: attendance.declined, cls: '' },
                  { key: 'awaiting', rows: attendance.awaiting, cls: '' },
                ]
                  .filter((b) => b.always || b.rows.length > 0)
                  .map((b, i) => (
                    <React.Fragment key={b.key}>
                      {i > 0 && ' · '}
                      {/* The count is the summary; the tooltip is the detail.
                          `disabled` when the bucket is empty — a tooltip that
                          opens on "0 going" to say nothing is worse than none. */}
                      <Tooltip
                        side="bottom"
                        disabled={b.rows.length === 0}
                        content={
                          <span style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                            {b.rows.map((r) => {
                              const label =
                                r.user?.name ||
                                r.user?.email ||
                                t('calendar.invite.userN', { id: r.user_id });
                              return (
                                <span
                                  key={r.id ?? r.user_id}
                                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                                >
                                  {/* Same face as everywhere else — served by the
                                      IdP, falling back to initials-on-colour when
                                      there is no picture or the app is offline. */}
                                  <UserAvatar userId={r.user_id} name={label} size={18} />
                                  <span>{label}</span>
                                </span>
                              );
                            })}
                          </span>
                        }
                      >
                        <span className={b.cls} style={{ cursor: b.rows.length ? 'default' : undefined }}>
                          {t(`calendar.attendance.${b.key}`, { count: b.rows.length })}
                        </span>
                      </Tooltip>
                    </React.Fragment>
                  ))}
              </span>
              {attendance.faces.length > 0 && (
                <AvatarStack members={attendance.faces} size={16} />
              )}
            </div>
          </div>
        )}
        {event.location && (
          <div className="cal-popover-row">
            <MapPin size={13} className="cal-popover-ico" />
            <span>{event.location}</span>
          </div>
        )}
        {event.description && (
          <div className="cal-popover-row">
            <AlignLeft size={13} className="cal-popover-ico" />
            <span style={{ whiteSpace: 'pre-wrap' }}>{event.description}</span>
          </div>
        )}
        {Array.isArray(event.reminders) && event.reminders.length > 0 && (
          <div className="cal-popover-row">
            <Bell size={13} className="cal-popover-ico" />
            <span>
              {event.reminders
                .map((r) =>
                  r.minutes_before === 0
                    ? t('calendar.reminder.atStart')
                    : t('calendar.reminder.minBefore', { count: Number(r.minutes_before) })
                )
                .join(', ')}
            </span>
          </div>
        )}
        {event.created_by?.name && (
          <div className="cal-popover-row">
            <span>{t('calendar.popover.createdBy', { name: event.created_by.name })}</span>
          </div>
        )}
        {event.pending_invite ? (
          <>
            <div className="cal-popover-row">
              <span style={{ fontStyle: 'italic' }}>
                {event.pending_invite.invited_by?.name
                  ? t('calendar.invite.pendingFrom', {
                      name: event.pending_invite.invited_by.name,
                    })
                  : t('calendar.invite.pendingInvitation')}
              </span>
            </div>
            <div className="cal-popover-sep" />
            <div className="cal-popover-actions">
              <button
                type="button"
                className="cal-btn"
                onClick={() => onRespondInvite?.(event, 'decline')}
              >
                {t('calendar.invite.decline')}
              </button>
              <button
                type="button"
                className="cal-btn cal-btn--primary"
                onClick={() => onRespondInvite?.(event, 'accept')}
              >
                {t('calendar.invite.accept')}
              </button>
            </div>
          </>
        ) : readOnly ? (
          <div className="cal-popover-row">
            <span style={{ fontStyle: 'italic' }}>{t('calendar.popover.viewOnlyTeam')}</span>
          </div>
        ) : (
          <>
          <div className="cal-popover-sep" />
          <div className="cal-popover-actions">
            <button type="button" className="cal-btn" onClick={() => onEdit?.(event)}>
              <Pencil size={13} />
              {t('common.edit')}
            </button>
            <button type="button" className="cal-btn" onClick={() => onDelete?.(event)}>
              <Trash2 size={13} />
              {t('common.delete')}
            </button>
          </div>
          </>
        )}
      </div>
    </>,
    document.body
  );
}
